import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AskIcon from 'shared/components/ui/AskIcon';
import DrawerTab from 'shared/components/ui/DrawerTab';
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Search,
  LayoutGrid,
  Zap,
  User,
  AlertTriangle,
  Layers,
  Radio,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import axiosInstance from "shared/services/api/axiosInstance";
import { getSelectedTeamNumericId } from "shared/utils/selectedTeamId";
import { LEGACY_STUDIO_UI } from "shared/config/featureFlags";
import { payloadPreviewText } from "features/agent-analytics/components/runCells";
import { openUserProfile } from "features/users/openUserProfile";
import { colorForKind } from "shared/components/spans/KindTag";

const RECENTS_KEY = "trodo.globalSearch.recents";
const MAX_RECENTS = 12;

/**
 * Tab order is the mental model: people first, then activity, then the agent
 * stack, then analysis artefacts. 'all' fronts the top of each.
 */
const TABS = [
  { key: "all", label: "All" },
  { key: "users", label: "Users" },
  { key: "events", label: "Events" },
  { key: "runs", label: "Runs" },
  { key: "spans", label: "Spans" },
  { key: "capabilities", label: "Capabilities" },
  { key: "signals", label: "Signals" },
  { key: "issues", label: "Issues" },
  { key: "annotations", label: "Annotations" },
  { key: "boards", label: "Boards" },
];

const KIND_ICON = {
  user: User,
  event: Zap,
  // Runs and spans wear the trace tree's marks, not generic play / branch.
  run: colorForKind("run").Glyph,
  span: colorForKind("span").Glyph,
  capability: Layers,
  signal: Radio,
  issue: AlertTriangle,
  annotation: MessageSquare,
  board: LayoutGrid,
  recent: ChevronRight,
  shortcut: ChevronRight,
};

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRecents(items) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(items.slice(0, MAX_RECENTS)));
  } catch {
    /* ignore */
  }
}

function pushRecent(entry) {
  const prev = readRecents().filter((r) => r.href !== entry.href);
  writeRecents([{ ...entry, at: Date.now() }, ...prev]);
}

/** Local <mark> for the result list itself (the destination pages use
 *  shared/components/HighlightText, which is context-driven). */
function Marked({ text, q }) {
  const s = String(text ?? "");
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return s;
  const idx = s.toLowerCase().indexOf(needle);
  if (idx === -1) return s;
  return (
    <>
      {s.slice(0, idx)}
      <mark
        className="rounded-sm px-px"
        style={{ backgroundColor: "var(--feedback-warning-bg, rgba(250,204,21,0.35))", color: "inherit" }}
      >
        {s.slice(idx, idx + needle.length)}
      </mark>
      {s.slice(idx + needle.length)}
    </>
  );
}

/** Trim a payload preview so the match sits inside the visible window. */
function matchWindow(text, q, span = 110) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  const idx = q ? s.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (idx <= 30) return s.slice(0, span) + (s.length > span ? "…" : "");
  const start = Math.max(0, idx - 40);
  return "…" + s.slice(start, start + span) + (start + span < s.length ? "…" : "");
}

/**
 * Global search palette.
 *
 * One backend round-trip (GET /global-search) returns every category,
 * each searched and ranked server-side — runs and spans hybrid
 * keyword+semantic over input AND output embeddings. Results live under
 * tabs (Users / Events / Runs / Spans / Capabilities / Signals / Issues /
 * Boards); the All tab shows the top few of each. Boards match client-side —
 * the boards list is already in memory.
 *
 * Opening a result opens the THING, not its list page: runs/spans/events/UX
 * signals open their panels with the query highlighted inside; capabilities
 * deep-link into their drawer; users land on their profile with the match
 * marked.
 */
export default function GlobalSearchModal({ open, onClose, teamSlug, boards, loadBoards, onOpenRun, onOpenSpan, onOpenEvent, onOpenUxSignal }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(0);
  const [recents, setRecents] = useState(() => readRecents());
  const [results, setResults] = useState(null); // null = nothing searched yet
  const [loading, setLoading] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState(null);

  const teamNumericId = getSelectedTeamNumericId();
  const encTeam = encodeURIComponent(teamSlug);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setTab("all");
    setSelected(0);
    setResults(null);
    setExpandedIssue(null);
    setRecents(readRecents());
    if (teamNumericId != null && typeof loadBoards === "function") {
      loadBoards({ teamId: teamNumericId });
    }
  }, [open, teamNumericId, loadBoards]);

  // ── The one debounced backend call ────────────────────────────────────
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  useEffect(() => {
    if (!open || teamNumericId == null) return undefined;
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) {}
      abortRef.current = null;
    }
    if (q.length < 2) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const { data } = await axiosInstance.get("/global-search", {
          params: { team_id: teamNumericId, q, limit: 10 },
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        setResults(data);
      } catch (e) {
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
        setResults({ users: [], events: [], runs: [], spans: [], capabilities: [], signals: [], issues: [], annotations: [], modes: {} });
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) { try { abortRef.current.abort(); } catch (_) {} }
    };
  }, [query, open, teamNumericId]);

  const qRaw = query.trim();

  // ── Rows per category, normalised to one row shape ────────────────────
  const rowsByTab = useMemo(() => {
    const out = { users: [], events: [], runs: [], spans: [], capabilities: [], signals: [], issues: [], annotations: [], boards: [] };
    if (!qRaw) return out;

    // Boards: matched here — the list is already loaded in memory.
    const ql = qRaw.toLowerCase();
    for (const b of boards || []) {
      const name = b.name || "Untitled";
      if (!name.toLowerCase().includes(ql)) continue;
      const href = `/${encTeam}/analytics/boards/${encodeURIComponent(b.id)}`;
      out.boards.push({
        id: `board-${b.id}`, kind: "board", title: name, meta: "Report board", href,
        onSelect: () => navigate(href),
      });
    }

    if (!results) return out;

    for (const u of results.users || []) {
      const id = u.distinct_id || u.user_id;
      if (!id) continue;
      const href = `/${encTeam}/activity/users/${encodeURIComponent(String(id))}?highlight=${encodeURIComponent(qRaw)}`;
      out.users.push({
        id: `user-${id}`, kind: "user",
        title: String(id),
        meta: [u.name, u.email].filter(Boolean).join(" · ") || "User profile",
        href,
        // The user drawer opens over whatever page search was opened on.
        onSelect: () => openUserProfile(String(id)),
      });
    }

    for (const ev of results.events || []) {
      const name = ev.event_name || "";
      const href = `/${encTeam}/activity/events?eventName=${encodeURIComponent(name)}`;
      out.events.push({
        id: `ev-${name}`, kind: "event",
        title: name,
        meta: ev.count != null ? `${Number(ev.count).toLocaleString()} occurrences` : "Event",
        href,
        onSelect: () => {
          if (onOpenEvent) onOpenEvent({ eventName: name, summary: ev, highlight: qRaw });
          else navigate(href);
        },
      });
    }

    for (const r of results.runs || []) {
      if (!r?.run_id) continue;
      const preview = matchWindow(
        payloadPreviewText(r.input_preview || r.output_preview),
        qRaw,
      );
      out.runs.push({
        id: `run-${r.run_id}`, kind: "run",
        title: r.agent_name || `Run ${String(r.run_id).slice(0, 10)}`,
        meta: [r.status, r.match_type === "semantic" ? null : `matched ${r.match_field}`]
          .filter(Boolean).join(" · ") || "Agent run",
        snippet: preview,
        semantic: r.match_type === "semantic",
        onSelect: () => {
          if (onOpenRun) onOpenRun(String(r.run_id), qRaw);
          else navigate(`/${encTeam}/agent-analytics/runs?q=${encodeURIComponent(qRaw)}`);
        },
      });
    }

    for (const sp of results.spans || []) {
      if (!sp?.span_id) continue;
      const preview = matchWindow(
        payloadPreviewText(sp.input_preview || sp.output_preview),
        qRaw,
      );
      out.spans.push({
        id: `span-${sp.span_id}`, kind: "span",
        title: sp.name || sp.tool_name || sp.kind || `Span ${String(sp.span_id).slice(0, 10)}`,
        meta: [sp.agent_name, sp.kind, sp.match_type === "semantic" ? null : `matched ${sp.match_field}`]
          .filter(Boolean).join(" · ") || "Span",
        snippet: preview,
        semantic: sp.match_type === "semantic",
        onSelect: () => {
          if (onOpenSpan) onOpenSpan(String(sp.span_id), qRaw);
          else if (onOpenRun && sp.run_id) onOpenRun(String(sp.run_id), qRaw);
        },
      });
    }

    for (const c of results.capabilities || []) {
      if (c?.id == null) continue;
      const href = `/${encTeam}/agent-analytics/capabilities?capabilityId=${encodeURIComponent(String(c.id))}&highlight=${encodeURIComponent(qRaw)}`;
      out.capabilities.push({
        id: `cap-${c.id}`, kind: "capability",
        title: c.name || `Capability #${c.id}`,
        meta: [c.agent_name, c.run_count != null ? `${c.run_count} runs` : null].filter(Boolean).join(" · ") || "Capability",
        snippet: c.definition ? matchWindow(c.definition, qRaw) : null,
        href,
        onSelect: () => navigate(href),
      });
    }

    for (const s of results.signals || []) {
      const isUx = s.kind === "ux";
      // A detector signal without a run/span anchor has no panel to open —
      // it expands in place instead of closing the palette into nothing.
      const anchorless = !isUx && !s.run_id && !s.span_id;
      out.signals.push({
        id: `sig-${s.kind}-${s.id}`, kind: "signal",
        title: isUx ? (s.signal_type || "UX signal") : (s.pattern || s.category || "Signal"),
        meta: isUx
          ? [s.current_url, s.distinct_id].filter(Boolean).join(" · ") || "UX signal"
          : [s.category, s.run_id ? "on run" : s.span_id ? "on span" : null].filter(Boolean).join(" · ") || "Signal",
        snippet: s.text ? matchWindow(s.text, qRaw) : null,
        expandable: anchorless
          ? { id: `sig-${s.id}`, Signal: s.pattern || s.category, Detail: s.text, When: s.occurred_at ? new Date(s.occurred_at).toLocaleString() : null }
          : null,
        onSelect: () => {
          if (anchorless) {
            setExpandedIssue((prev) => (prev === `sig-${s.id}` ? null : `sig-${s.id}`));
          } else if (isUx) {
            if (onOpenUxSignal) onOpenUxSignal(s.id);
          } else if (s.run_id && onOpenRun) {
            onOpenRun(String(s.run_id), qRaw);
          } else if (s.span_id && onOpenSpan) {
            onOpenSpan(String(s.span_id), qRaw);
          }
        },
      });
    }

    for (const issue of results.issues || []) {
      if (!issue?.id) continue;
      out.issues.push({
        id: `issue-${issue.id}`, kind: "issue",
        title: matchWindow(issue.failure, qRaw, 90),
        meta: [issue.agent_name, issue.confidence_score != null ? `confidence ${Math.round(Number(issue.confidence_score) * 100)}%` : null]
          .filter(Boolean).join(" · ") || "Issue",
        // Issues expand in place: the v5 read API isn't built yet, so the
        // palette shows the analysis itself rather than a dead-end nav.
        expandable: {
          id: `issue-${issue.id}`,
          Failure: issue.failure,
          Routing: issue.routing_decision,
          Agent: issue.agent_name,
        },
        onSelect: () => setExpandedIssue((prev) => (prev === `issue-${issue.id}` ? null : `issue-${issue.id}`)),
      });
    }

    // A comment opens what it is ABOUT — the span if it was left on one,
    // otherwise the run. The remark is the title, because that is the text
    // you searched for and the thing you will recognise.
    for (const a of results.annotations || []) {
      if (!a?.id) continue;
      out.annotations.push({
        id: `annotation-${a.id}`, kind: "annotation",
        title: matchWindow(a.body, qRaw, 90),
        meta: [a.author_name, `on ${a.context_type}`].filter(Boolean).join(" · "),
        onSelect: () => {
          if (a.context_type === "span" && a.span_id && onOpenSpan) onOpenSpan(String(a.span_id), qRaw);
          else if (a.run_id && onOpenRun) onOpenRun(String(a.run_id), qRaw);
        },
      });
    }

    return out;
  }, [qRaw, results, boards, encTeam, navigate, onOpenRun, onOpenSpan, onOpenEvent, onOpenUxSignal]);

  const counts = useMemo(() => {
    const c = {};
    let all = 0;
    for (const t of TABS) {
      if (t.key === "all") continue;
      c[t.key] = rowsByTab[t.key]?.length || 0;
      all += c[t.key];
    }
    c.all = all;
    return c;
  }, [rowsByTab]);

  // ── The visible list: active tab's rows, or grouped top-3s on All ─────
  const listRows = useMemo(() => {
    if (!qRaw) {
      const recentRows = recents.slice(0, 8).map((r) => ({
        id: `rc-${r.href}`, kind: "recent", title: r.title, meta: r.meta || "Recent", href: r.href,
        onSelect: () => navigate(r.href),
      }));
      const shortcuts = [
        { id: "sh-ask", title: "Lucid", meta: "App", href: `/${encTeam}/chat` },
        LEGACY_STUDIO_UI
          ? { id: "sh-reports", title: "Reports", meta: "Analytics", href: `/${encTeam}/analytics/reports` }
          : { id: "sh-reports", title: "Reports", meta: "Assess", href: `/${encTeam}/reports` },
        { id: "sh-events", title: "Live events", meta: "Activity", href: `/${encTeam}/activity/events` },
        { id: "sh-runs", title: "Agent runs", meta: "Observe", href: `/${encTeam}/agent-analytics/runs` },
      ].map((s) => ({ ...s, kind: "shortcut", onSelect: () => navigate(s.href) }));
      return [...recentRows, ...shortcuts];
    }
    if (tab === "all") {
      const rows = [];
      for (const t of TABS) {
        if (t.key === "all") continue;
        const bucket = rowsByTab[t.key] || [];
        if (!bucket.length) continue;
        rows.push({ id: `hdr-${t.key}`, kind: "header", title: t.label, tabKey: t.key, count: bucket.length });
        rows.push(...bucket.slice(0, 3));
      }
      return rows;
    }
    return rowsByTab[tab] || [];
  }, [qRaw, tab, rowsByTab, recents, encTeam, navigate]);

  const selectableIdx = useMemo(
    () => listRows.map((r, i) => (r.kind === "header" ? null : i)).filter((i) => i != null),
    [listRows],
  );

  useEffect(() => { setSelected(selectableIdx[0] ?? 0); }, [query, tab, listRows.length, selectableIdx]);

  const runSelect = useCallback(
    (row) => {
      if (!row?.onSelect) return;
      if (row.kind === "header") { setTab(row.tabKey); return; }
      const staysOpen = row.expandable != null;
      if (row.href && !row.expandable) {
        pushRecent({ title: typeof row.title === "string" ? row.title : qRaw, meta: row.meta, href: row.href });
        setRecents(readRecents());
      }
      row.onSelect();
      if (!staysOpen) { onClose(); setQuery(""); }
    },
    [onClose, qRaw],
  );

  // ── Keys: ↑↓ move, Enter opens, Tab cycles tabs, Esc closes ───────────
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "Tab" && qRaw) {
        e.preventDefault();
        const keys = TABS.map((t) => t.key);
        const cur = keys.indexOf(tab);
        const next = keys[(cur + (e.shiftKey ? keys.length - 1 : 1)) % keys.length];
        setTab(next);
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const pos = selectableIdx.indexOf(selected);
        const nextPos = e.key === "ArrowDown"
          ? Math.min(pos + 1, selectableIdx.length - 1)
          : Math.max(pos - 1, 0);
        setSelected(selectableIdx[nextPos] ?? 0);
      }
      if (e.key === "Enter" && listRows[selected]) {
        e.preventDefault();
        runSelect(listRows[selected]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, listRows, selected, selectableIdx, runSelect, tab, qRaw]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selected}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selected]);

  if (!open) return null;

  const semanticActive = results?.modes?.runs === "semantic" || results?.modes?.runs === "hybrid"
    || results?.modes?.spans === "semantic" || results?.modes?.spans === "hybrid";

  const el = (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[10vh]"
      style={{ background: "color-mix(in srgb, var(--text-primary) 40%, transparent)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="global-search-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl"
        style={{
          borderColor: "var(--border-default)",
          background: "var(--surface-sunken)",
          fontFamily: "Inter, sans-serif",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="global-search-title" className="sr-only">Search workspace</h2>

        {/* Input row */}
        <div className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: "var(--border-default)" }}>
          <Search className="h-5 w-5 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search runs, spans, users, issues…"
            className="min-w-0 flex-1 bg-transparent text-[15px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {loading ? (
            <span className="text-[11px] text-[color:var(--text-muted)]">Searching…</span>
          ) : semanticActive ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--text-muted)]" title="Runs and spans matched by meaning (embeddings), not just text">
              <AskIcon size={14} /> semantic
            </span>
          ) : null}
          <kbd className="hidden rounded border px-1.5 py-0.5 text-[11px] text-[color:var(--text-muted)] sm:inline" style={{ borderColor: "var(--border-default)" }}>
            Esc
          </kbd>
        </div>

        {/* Tabs: only meaningful once there's a query */}
        {qRaw ? (
          <div className="flex items-center gap-1 overflow-x-auto border-b px-2" style={{ borderColor: "var(--border-default)" }} role="tablist" aria-label="Result categories">
            {TABS.map((t) => {
              const n = counts[t.key] || 0;
              return (
                <DrawerTab
                  key={t.key}
                  id={t.key}
                  role="tab"
                  label={t.label}
                  badge={n > 0 ? n : null}
                  active={t.key === tab}
                  onClick={setTab}
                />
              );
            })}
          </div>
        ) : null}

        {/* Result list */}
        <div className="max-h-[min(480px,60vh)] overflow-y-auto py-2" ref={listRef}>
          {!qRaw ? (
            <div className="px-4 pb-1">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                {recents.length ? "Recents" : "Go to"}
              </div>
            </div>
          ) : null}

          {qRaw && !loading && listRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">
              No matches{tab !== "all" ? ` in ${TABS.find((t) => t.key === tab)?.label}` : ""}. Try another keyword or tab.
            </div>
          ) : null}

          {listRows.map((row, idx) => {
            if (row.kind === "header") {
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setTab(row.tabKey)}
                  className="mt-1 flex w-full items-center justify-between px-4 py-1 text-left"
                >
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{row.title}</span>
                  {row.count > 3 ? (
                    <span className="text-[10px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
                      View all {row.count} →
                    </span>
                  ) : null}
                </button>
              );
            }
            const active = idx === selected;
            const Icon = KIND_ICON[row.kind] || LayoutGrid;
            const expanded = row.expandable && expandedIssue === row.expandable.id;
            return (
              <div key={row.id} data-idx={idx}>
                <button
                  type="button"
                  onMouseEnter={() => setSelected(idx)}
                  onClick={() => runSelect(row)}
                  className={`flex w-full items-start gap-3 px-4 py-2 text-left transition-colors ${
                    active ? "bg-surface-hover" : ""
                  }`}
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[color:var(--border-default)] bg-surface text-[color:var(--text-muted)]">
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-[color:var(--text-primary)]">
                      <Marked text={row.title} q={qRaw} />
                    </span>
                    {row.snippet ? (
                      <span className="block truncate text-[11px] text-[color:var(--text-secondary)]">
                        <Marked text={row.snippet} q={qRaw} />
                      </span>
                    ) : null}
                    <span className="block truncate text-[11px] text-[color:var(--text-muted)]">
                      {row.meta}
                      {row.semantic ? (
                        <span className="ml-1.5 inline-flex items-center gap-0.5" title="Matched by meaning, not literal text">
                          <AskIcon size={12} className="inline" /> semantic
                        </span>
                      ) : null}
                    </span>
                  </span>
                </button>
                {expanded ? (
                  <div className="mx-4 mb-2 rounded-lg border border-[color:var(--border-default)] bg-surface px-3 py-2 text-[12px] leading-relaxed">
                    <DetailInline data={row.expandable} q={qRaw} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="border-t px-4 py-2 text-[11px] text-[color:var(--text-muted)]" style={{ borderColor: "var(--border-default)" }}>
          <span className="hidden sm:inline">↑↓ move · Enter open · Tab switch category · </span>
          <span>Ctrl K</span>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(el, document.body) : null;
}

/** In-place detail for rows with nowhere to navigate — issues (the v5 read
 *  API isn't built yet) and detector signals without a run/span anchor. The
 *  palette shows the content itself instead of closing into a dead end. */
function DetailInline({ data, q }) {
  const rows = Object.entries(data).filter(([k, v]) => k !== "id" && v);
  return (
    <div className="space-y-1.5">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{label}</span>
          <div className="text-[color:var(--text-primary)]"><Marked text={String(value)} q={q} /></div>
        </div>
      ))}
    </div>
  );
}
